/**
 * The connectivity state (PLAN §10.2) and the pure decisions over it: when an
 * endpoint counts as down or stably up, when to probe next, and which
 * endpoint is the primary backend. The manager (`manager.ts`) holds one of
 * these and feeds it probe results; everything here is a function of its
 * inputs, so the failover rules are unit-tested with a fake clock.
 */
import type { ProbeFailure } from '@norna/api-client/health.js';
import type { StatusResponseWire } from '@norna/api-contract/statusRoutes.js';
import type { WitnessInForceWire } from '@norna/api-contract/witness.js';
import type { Endpoint } from './endpoints.js';
import type { PairNodeRoleWire } from '@norna/api-contract/witness.js';
import type { CloudLinkStateWire, PeerLinkStateWire } from '@norna/api-contract/primitives.js';
import type { LocalNetworkAccess } from './localNetwork.js';

/** `unknown` until the first probe answers (or fails twice). */
export type Reach = 'unknown' | 'up' | 'down';

/** The primary's WebSocket, as the view holding it reports (`reportSocket`); `unknown` while nothing subscribes. */
export type SocketState = 'unknown' | 'connecting' | 'open' | 'closed';

export type NodeReport = {
  readonly nodeId: string;
  /** Its site (a node-served page with no studios names it: "assign a studio to …"). */
  readonly siteId: string;
  readonly cloudLink: CloudLinkStateWire;
  readonly outboxDepth: number;
  /** In a main/backup pair: which witness decides a failover and how long one takes (wave 4c); null otherwise. */
  readonly witness: WitnessInForceWire | null;
  /** In a main/backup pair: the node's place, the peer link, and the productions it runs active and standby (wave 7b-tail C); null otherwise. */
  readonly pair: PairSummary | null;
  /** Productions it runs that the cloud does not know as its site's (`/health`'s `cloudRefusals`, docs/sitelink.md); empty for none. */
  readonly cloudRefusals: readonly NodeCloudRefusal[];
};

/** A production a node runs that the cloud refuses: why, and the way out. */
export type NodeCloudRefusal = { readonly productionId: string; readonly reason: string; readonly fix: string };

export type PairSummary = {
  /** As the node's `/health` says; `unknown` from a newer node (D37), shown as such. */
  readonly nodeRole: PairNodeRoleWire;
  readonly peerLink: PeerLinkStateWire;
  readonly active: number;
  readonly standby: number;
  /** Productions with an alarm on this node. */
  readonly alarms: number;
};

export type EndpointStatus = {
  readonly endpoint: Endpoint;
  readonly reach: Reach;
  /** The last probe's round trip (ms), answered or not; null before any. */
  readonly latencyMs: number | null;
  readonly version: string | null;
  readonly stage: string | null;
  readonly checkedAtMs: number | null;
  readonly lastOkAtMs: number | null;
  /** Consecutive failed probes (0 after a success). */
  readonly failures: number;
  /** Consecutive successful probes (0 after a failure). */
  readonly successes: number;
  /** Why the last probe failed, for the panel; null after a success. */
  readonly error: string | null;
  /** How the last probe failed (`timeout`: nothing answered in time; `network`: refused, DNS, TLS — a browser cannot tell which); null after a success. */
  readonly failure: ProbeFailure | null;
  /** What a node says about itself: its site link and outbox. Null for the cloud, or before a node answered. */
  readonly node: NodeReport | null;
  /** When the manager probes it next (its scheduled timer); null before the first probe answered. */
  readonly nextProbeAtMs: number | null;
  /** When its current run of failed probes began; null while it answers (or before any probe). */
  readonly failingSinceMs: number | null;
  /**
   * What its API offers, as its last answered `/health.api.features` said (D37 §4.7): empty before it answered, and
   * for a host older than the negotiation — which then offers none of the features (`endpointHasFeature`).
   */
  readonly apiFeatures: readonly string[];
  /** The commit of the release a node runs, as its last `/health.release` said (D37); null for the cloud, before an answer, or from an older node. */
  readonly releaseCommit: string | null;
};

export type ConnectivityState = {
  readonly endpoints: readonly EndpointStatus[];
  /** Who served the page: decides the default preference (PLAN §10.2 endpoint selection). */
  readonly pageKind: 'cloud' | 'node';
  /** The primary backend's endpoint id: the data path. */
  readonly primaryId: string;
  /** The operator's explicit choice ("Switch to local"), or null for the default order. */
  readonly chosenId: string | null;
  readonly socket: SocketState;
  /** The cloud's view of the tenant's sites (`GET /status`), when it could be read. */
  readonly cloudView: StatusResponseWire | null;
  /** Whether this page may reach the studio servers' private addresses (Chrome's Local Network Access, `localNetwork.ts`). */
  readonly localNetwork: LocalNetworkAccess;
};

/**
 * Whether the endpoint's API offers `feature` (D37 §4.7, `@norna/api-contract/clientIdentity.js`' `API_FEATURES`): as
 * its last answered probe said. False before it answered — a feature is used only once the host said it has it.
 */
export function endpointHasFeature(state: Pick<ConnectivityState, 'endpoints'>, endpointId: string, feature: string): boolean {
  return state.endpoints.find((s) => s.endpoint.id === endpointId)?.apiFeatures.includes(feature) ?? false;
}

/** Two failures in a row mark an endpoint down: one lost probe on a busy LAN is not an outage. */
export const DOWN_AFTER_FAILURES = 2;
/** Two successes in a row before the SPA moves its primary BACK to a preferred endpoint: no flapping. */
export const STABLE_AFTER_SUCCESSES = 2;
/** How often a healthy endpoint is probed. */
export const HEALTHY_INTERVAL_MS = 10_000;
/** Probing a failing endpoint backs off from this… */
export const RETRY_BASE_MS = 1_000;
/** …to this. Short enough that a node back from a restart is noticed within seconds. */
export const RETRY_MAX_MS = 15_000;
/**
 * An endpoint that is not on the data path and has failed every probe for
 * this long is dormant: a node record an old run left behind, whose name no
 * longer resolves, is not worth a failed request (and a console error) every
 * 15 s for ever. Long enough that a node restarting when the page loads is
 * never dormant.
 */
export const DORMANT_AFTER_MS = 5 * 60_000;
/** A dormant endpoint's probes keep doubling past `RETRY_MAX_MS`, to this. It still shows as unreachable. */
export const DORMANT_MAX_MS = 5 * 60_000;

export function initialStatus(endpoint: Endpoint): EndpointStatus {
  return { endpoint, reach: 'unknown', latencyMs: null, version: null, stage: null, checkedAtMs: null, lastOkAtMs: null, failures: 0, successes: 0, error: null, failure: null, node: null, nextProbeAtMs: null, failingSinceMs: null, apiFeatures: [], releaseCommit: null };
}

/** When to probe an endpoint again, after `failures` consecutive failures (0: it is healthy). */
export function nextProbeDelayMs(failures: number): number {
  if (failures <= 0) return HEALTHY_INTERVAL_MS;
  return Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (failures - 1));
}

/**
 * When to probe `status` again, at `nowMs`. `onDataPath`: it is the primary,
 * the operator's choice, or the page's own backend — an outage there must be
 * seen to end within seconds, so it never goes dormant. Anything else that has
 * failed every probe for `DORMANT_AFTER_MS` (`isDormant`) keeps doubling to
 * `DORMANT_MAX_MS` instead of stopping at `RETRY_MAX_MS`; one failing for less
 * keeps the short cap, so a node back from a restart is noticed at once. (The
 * manager also wakes the dormant ones when the primary goes down, and probes
 * an endpoint the operator chooses: `manager.ts`.)
 */
export function probeDelayMs(status: EndpointStatus, onDataPath: boolean, nowMs: number): number {
  const short = nextProbeDelayMs(status.failures);
  if (status.failures <= 0 || onDataPath || !isDormant(status, nowMs)) return short;
  return Math.min(DORMANT_MAX_MS, RETRY_BASE_MS * 2 ** (status.failures - 1));
}

/** Failing every probe for `DORMANT_AFTER_MS` or more. */
export function isDormant(status: EndpointStatus, nowMs: number): boolean {
  return status.failingSinceMs !== null && nowMs - status.failingSinceMs >= DORMANT_AFTER_MS;
}

export type ProbeOutcome =
  | { readonly ok: true; readonly latencyMs: number; readonly version: string; readonly stage: string; readonly node: NodeReport | null; readonly features?: readonly string[]; readonly releaseCommit?: string | null }
  | { readonly ok: false; readonly latencyMs: number; readonly error: string; readonly failure: ProbeFailure };

/** An endpoint after one probe at `nowMs`. */
export function applyProbe(status: EndpointStatus, outcome: ProbeOutcome, nowMs: number): EndpointStatus {
  if (outcome.ok) {
    return {
      ...status,
      reach: 'up',
      latencyMs: outcome.latencyMs,
      version: outcome.version,
      stage: outcome.stage,
      checkedAtMs: nowMs,
      lastOkAtMs: nowMs,
      failures: 0,
      successes: status.successes + 1,
      error: null,
      failure: null,
      node: outcome.node,
      failingSinceMs: null,
      apiFeatures: outcome.features ?? [],
      releaseCommit: outcome.releaseCommit ?? null,
    };
  }
  const failures = status.failures + 1;
  return {
    ...status,
    reach: failures >= DOWN_AFTER_FAILURES ? 'down' : status.reach,
    latencyMs: outcome.latencyMs,
    checkedAtMs: nowMs,
    failures,
    successes: 0,
    error: outcome.error,
    failure: outcome.failure,
    failingSinceMs: status.failingSinceMs ?? nowMs,
  };
}

/**
 * A data request to this endpoint just failed at the network level. It is
 * SUSPECT: no longer `up` (so nothing resends to it before a probe says so),
 * and one failure short of down, so the probe the manager runs at once decides
 * on its own — a failure there makes it down, a success makes it up again.
 */
export function applyRequestFailure(status: EndpointStatus): EndpointStatus {
  return { ...status, reach: status.reach === 'down' ? 'down' : 'unknown', failures: Math.max(status.failures, DOWN_AFTER_FAILURES - 1), successes: 0 };
}

/**
 * The order in which endpoints are preferred as the primary (only those that `canBePrimary`):
 *   - the operator's choice first, when there is one;
 *   - a page served by a node: the nodes (as listed, the serving one first), then the cloud;
 *   - a page served by the cloud: the cloud, then the nodes — the SPA OFFERS the switch to
 *     local rather than making it (PLAN §10.2), but still fails over to a node if the cloud drops.
 */
export function preferenceOrder(state: Pick<ConnectivityState, 'endpoints' | 'pageKind' | 'chosenId'>): string[] {
  const ids = (kind: 'cloud' | 'node') => state.endpoints.filter((s) => s.endpoint.kind === kind && s.endpoint.canBePrimary).map((s) => s.endpoint.id);
  const base = state.pageKind === 'node' ? [...ids('node'), ...ids('cloud')] : [...ids('cloud'), ...ids('node')];
  const chosen = state.chosenId;
  return chosen !== null && base.includes(chosen) ? [chosen, ...base.filter((id) => id !== chosen)] : base;
}

/**
 * The primary backend, given the current one:
 *   1. a more preferred endpoint that is STABLY up (STABLE_AFTER_SUCCESSES) wins — moving back
 *      to it after a failover, or to the operator's choice;
 *   2. otherwise the current primary stays, unless it is down;
 *   3. a down primary gives way to the most preferred endpoint that is up;
 *   4. with nothing up, the current primary stays (there is nothing better to switch to).
 * `unknown` is not down: at start-up the page's own backend is the primary until proven otherwise.
 */
export function choosePrimary(state: Pick<ConnectivityState, 'endpoints' | 'pageKind' | 'chosenId' | 'primaryId'>): string {
  const order = preferenceOrder(state);
  const byId = new Map(state.endpoints.map((s) => [s.endpoint.id, s]));
  const current = byId.get(state.primaryId);
  const currentRank = order.indexOf(state.primaryId);
  for (const id of order.slice(0, currentRank === -1 ? order.length : currentRank)) {
    const s = byId.get(id);
    if (s !== undefined && s.reach === 'up' && s.successes >= STABLE_AFTER_SUCCESSES) return id;
  }
  if (current !== undefined && current.reach !== 'down') return state.primaryId;
  for (const id of order) if (byId.get(id)?.reach === 'up') return id;
  return state.primaryId;
}

/**
 * "Switch to local" (PLAN §10.2): offered on a cloud-served page whose primary
 * is the cloud, when a node is up — the id of the first such node, else null.
 */
export function localOffer(state: Pick<ConnectivityState, 'endpoints' | 'pageKind' | 'primaryId'>): string | null {
  if (state.pageKind !== 'cloud') return null;
  const primary = state.endpoints.find((s) => s.endpoint.id === state.primaryId);
  if (primary?.endpoint.kind !== 'cloud') return null;
  return state.endpoints.find((s) => s.endpoint.kind === 'node' && s.endpoint.canBePrimary && s.reach === 'up')?.endpoint.id ?? null;
}
